/**
 * ESC/POS — el idioma que hablan las impresoras térmicas.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  QUÉ ES ESTO Y POR QUÉ EXISTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hasta ahora Softfi imprimía con `window.print()`: el navegador abre su diálogo,
 * tú pulsas "Imprimir" y el sistema operativo se encarga. Funciona, pero:
 *
 *   - Sale un diálogo cada vez (dos toques extra por cada venta).
 *   - No corta el papel: hay que arrancarlo a mano.
 *   - No abre el cajón portamonedas.
 *   - A veces el navegador mete márgenes y el ticket sale descuadrado.
 *
 * ESC/POS es el idioma nativo de las impresoras térmicas: en vez de mandarle una
 * página, le mandas BYTES con órdenes crudas — "ponte en negrita", "centra",
 * "corta el papel". El resultado es instantáneo y sin diálogos: la experiencia
 * de un punto de venta de verdad.
 *
 * Este archivo NO habla con ninguna impresora. Solo construye los bytes.
 * Eso lo hace probable sin hardware: se le pide un ticket y se revisa el
 * resultado. Quien conecta y envía es `impresora.ts`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LA TRAMPA DE LOS ACENTOS (esto es lo que rompe el 90% de los intentos)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Una impresora térmica NO entiende UTF-8. Entiende "páginas de códigos": tablas
 * de 256 caracteres donde el byte 164 significa "ñ" en una tabla y otra cosa en
 * otra. Si le mandas UTF-8 tal cual, "Peluquería" sale como "PeluquerÃ­a".
 *
 * Por eso aquí hacemos tres cosas, en este orden:
 *   1. Le decimos a la impresora qué tabla usar (comando ESC t).
 *   2. Traducimos cada letra al byte que le corresponde EN ESA TABLA.
 *   3. Si una letra no existe en la tabla, la escribimos sin tilde ("añ" → "an")
 *      antes de rendirnos y poner "?". Un ticket que dice "Peluqueria" se lee;
 *      uno que dice "Peluquer?a" parece roto.
 *
 * Usamos CP850 por defecto porque es la única tabla común que trae TAMBIÉN las
 * mayúsculas acentuadas (Á É Í Ó Ú). CP437, la otra habitual, no las tiene — y
 * los nombres de productos suelen escribirse en mayúsculas.
 */

// =============================================================================
//  Bytes de control ESC/POS
// =============================================================================

const ESC = 0x1b;
const GS = 0x1d;

/** Anchos de papel, en caracteres por línea con la fuente normal. */
export const COLUMNAS: Record<AnchoPapel, number> = {
  "58": 32,
  "80": 48,
};

export type AnchoPapel = "58" | "80";

/**
 * Tablas de caracteres. El número es el que espera el comando `ESC t n`.
 *
 * Si al imprimir salen símbolos raros en vez de acentos, es que tu impresora
 * numera las tablas distinto: se cambia desde Ajustes → Impresora y listo.
 */
export const PAGINAS_DE_CODIGOS = {
  cp850: 2,
  cp437: 0,
  win1252: 16,
} as const;

export type PaginaDeCodigos = keyof typeof PAGINAS_DE_CODIGOS;

// =============================================================================
//  Las tablas de traducción
// =============================================================================

/*
 * Cada cadena tiene EXACTAMENTE 128 caracteres: los que ocupan los bytes 128
 * a 255 de esa tabla. Los bytes 0-127 son ASCII y coinciden en todas, así que
 * no hace falta listarlos.
 */

const CP850 =
  "ÇüéâäàåçêëèïîìÄÅ" +
  "ÉæÆôöòûùÿÖÜø£Ø×ƒ" +
  "áíóúñÑªº¿®¬½¼¡«»" +
  "░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐" +
  "└┴┬├─┼ãÃ╚╔╩╦╠═╬¤" +
  "ðÐÊËÈıÍÎÏ┘┌█▄¦Ì▀" +
  "ÓßÔÒõÕµþÞÚÛÙýÝ¯´" +
  "­±‗¾¶§÷¸°¨·¹³²■ ";

const CP437 =
  "ÇüéâäàåçêëèïîìÄÅ" +
  "ÉæÆôöòûùÿÖÜ¢£¥₧ƒ" +
  "áíóúñÑªº¿⌐¬½¼¡«»" +
  "░▒▓│┤╡╢╖╕╣║╗╝╜╛┐" +
  "└┴┬├─┼╞╟╚╔╩╦╠═╬╧" +
  "╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀" +
  "αßΓπΣσµτΦΘΩδ∞φε∩" +
  "≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ";

/**
 * Windows-1252 en el rango que nos importa (160-255) es idéntico a Unicode:
 * la "ñ" es el carácter 241 y también el byte 241. Por eso no hace falta tabla.
 */
function bytesWin1252(letra: string): number | null {
  const codigo = letra.codePointAt(0);
  if (codigo === undefined) return null;
  if (codigo >= 0xa0 && codigo <= 0xff) return codigo;
  return null;
}

/** Construye el diccionario "letra → byte" a partir de una de las cadenas de arriba. */
function construirTabla(caracteres: string): Map<string, number> {
  const tabla = new Map<string, number>();
  // Recorremos con `[...cadena]` y no con índices numéricos porque algunos
  // símbolos de dibujo ocupan más de una posición en JavaScript.
  const letras = [...caracteres];
  for (let i = 0; i < letras.length; i++) {
    const letra = letras[i];
    if (letra !== undefined && !tabla.has(letra)) tabla.set(letra, 128 + i);
  }
  return tabla;
}

const TABLAS: Record<PaginaDeCodigos, Map<string, number> | null> = {
  cp850: construirTabla(CP850),
  cp437: construirTabla(CP437),
  win1252: null, // se resuelve con la función de arriba, no con tabla
};

/**
 * Convierte texto a los bytes que entiende la impresora.
 *
 * El "plan B" de quitar la tilde es deliberado: preferimos un ticket que diga
 * "Cafe con leche" a uno que diga "Caf? con leche". El cliente lee el primero
 * sin darse cuenta; el segundo parece un sistema roto.
 */
export function textoABytes(texto: string, pagina: PaginaDeCodigos): number[] {
  const tabla = TABLAS[pagina];
  const salida: number[] = [];

  for (const letra of texto) {
    const codigo = letra.codePointAt(0) ?? 63;

    // 1. ASCII puro: pasa directo.
    if (codigo < 128) {
      // El salto de línea de la impresora es \n (10). Ignoramos el \r de Windows
      // porque algunas impresoras lo interpretan como "vuelve al margen" y
      // sobreescriben la línea anterior.
      if (codigo !== 13) salida.push(codigo);
      continue;
    }

    // 2. ¿Existe la letra en la tabla de la impresora?
    const directo = tabla ? tabla.get(letra) : bytesWin1252(letra);
    if (directo !== undefined && directo !== null) {
      salida.push(directo);
      continue;
    }

    // 3. Plan B: quitarle la tilde y volver a intentar.
    //    "é" se descompone en "e" + acento; nos quedamos con la "e".
    const sinTilde = letra.normalize("NFD").replace(/\p{Diacritic}/gu, "");
    let resuelto = false;
    for (const base of sinTilde) {
      const codigoBase = base.codePointAt(0) ?? 63;
      if (codigoBase < 128) {
        salida.push(codigoBase);
        resuelto = true;
      }
    }
    if (resuelto) continue;

    // 4. Nos rendimos con esta letra.
    salida.push(63); // "?"
  }

  return salida;
}

// =============================================================================
//  El constructor de tickets
// =============================================================================

/**
 * Se usa encadenando órdenes, que se lee casi como el ticket que va a salir:
 *
 *   const t = new Ticket("80");
 *   t.iniciar().centrar().doble().texto("MI NEGOCIO").salto()
 *    .normal().izquierda().columnas("Café", "2,50")
 *    .avanzar(3).cortar();
 *   const bytes = t.bytes();
 */
export class Ticket {
  private datos: number[] = [];
  private readonly columnasPorLinea: number;
  private readonly pagina: PaginaDeCodigos;

  /** Multiplicador de ancho activo (1 o 2), para que `columnas()` cuadre igual. */
  private escalaAncho = 1;

  constructor(ancho: AnchoPapel = "80", pagina: PaginaDeCodigos = "cp850") {
    this.columnasPorLinea = COLUMNAS[ancho];
    this.pagina = pagina;
  }

  /** Los bytes listos para enviar. */
  bytes(): Uint8Array {
    return new Uint8Array(this.datos);
  }

  /** Cuántos caracteres caben ahora mismo (la mitad si el texto está a doble ancho). */
  get anchoLinea(): number {
    return Math.floor(this.columnasPorLinea / this.escalaAncho);
  }

  private crudo(...bytes: number[]): this {
    this.datos.push(...bytes);
    return this;
  }

  // --- Arranque ------------------------------------------------------------

  /**
   * Deja la impresora en un estado conocido y le fija la tabla de caracteres.
   *
   * Es imprescindible: si el ticket anterior quedó en negrita y a doble tamaño,
   * el siguiente saldría igual. `ESC @` borra todo eso.
   */
  iniciar(): this {
    this.escalaAncho = 1;
    return this.crudo(ESC, 0x40).crudo(ESC, 0x74, PAGINAS_DE_CODIGOS[this.pagina]);
  }

  // --- Alineación ----------------------------------------------------------

  izquierda(): this {
    return this.crudo(ESC, 0x61, 0);
  }

  centrar(): this {
    return this.crudo(ESC, 0x61, 1);
  }

  derecha(): this {
    return this.crudo(ESC, 0x61, 2);
  }

  // --- Estilo --------------------------------------------------------------

  negrita(activa = true): this {
    return this.crudo(ESC, 0x45, activa ? 1 : 0);
  }

  subrayado(activo = true): this {
    return this.crudo(ESC, 0x2d, activo ? 1 : 0);
  }

  /**
   * Tamaño del texto. `GS !` recibe un solo byte donde los 4 bits de arriba son
   * el ancho y los 4 de abajo el alto, ambos de 1 a 8.
   */
  tamano(ancho: 1 | 2 | 3, alto: 1 | 2 | 3): this {
    this.escalaAncho = ancho;
    return this.crudo(GS, 0x21, ((ancho - 1) << 4) | (alto - 1));
  }

  /** Texto grande, para el nombre del negocio y el TOTAL. */
  doble(): this {
    return this.tamano(2, 2);
  }

  /** Vuelve al tamaño y estilo normales. */
  normal(): this {
    return this.tamano(1, 1).negrita(false).subrayado(false);
  }

  // --- Contenido -----------------------------------------------------------

  /** Escribe texto sin saltar de línea. */
  texto(t: string): this {
    this.datos.push(...textoABytes(t, this.pagina));
    return this;
  }

  /** Escribe texto y salta de línea. */
  linea(t = ""): this {
    return this.texto(t).salto();
  }

  salto(cantidad = 1): this {
    for (let i = 0; i < cantidad; i++) this.datos.push(10);
    return this;
  }

  /**
   * Escribe una línea con algo a la izquierda y algo a la derecha, rellenando
   * el medio con espacios: "Café                      2,50".
   *
   * Es la forma de alinear precios en una impresora térmica: no hay tabuladores
   * ni tablas, solo caracteres de ancho fijo. Si el texto de la izquierda es tan
   * largo que no cabe, se recorta — porque si no, la impresora salta de línea
   * sola y el precio termina en la línea de abajo, descolgado.
   */
  columnas(izquierda: string, derecha: string, relleno = " "): this {
    const total = this.anchoLinea;
    const der = derecha.slice(0, total);
    const espacioIzq = total - der.length;
    const izq = izquierda.length > espacioIzq ? izquierda.slice(0, espacioIzq) : izquierda;
    const huecos = Math.max(0, total - izq.length - der.length);
    return this.linea(izq + relleno.repeat(huecos) + der);
  }

  /**
   * Escribe un texto largo partiéndolo por palabras, para que no lo corte la
   * impresora a mitad de palabra.
   */
  parrafo(t: string): this {
    const total = this.anchoLinea;
    let actual = "";

    for (const palabra of t.split(/\s+/).filter(Boolean)) {
      if (actual === "") {
        actual = palabra;
      } else if (actual.length + 1 + palabra.length <= total) {
        actual += " " + palabra;
      } else {
        this.linea(actual);
        actual = palabra;
      }
      // Una palabra sola más larga que la línea (un correo, una URL): se parte.
      while (actual.length > total) {
        this.linea(actual.slice(0, total));
        actual = actual.slice(total);
      }
    }

    if (actual !== "") this.linea(actual);
    return this;
  }

  /** Línea de guiones de lado a lado. Separa bloques del ticket. */
  separador(caracter = "-"): this {
    return this.linea(caracter.repeat(this.anchoLinea));
  }

  // --- Cierre --------------------------------------------------------------

  /** Avanza papel. Antes de cortar hacen falta ~3 líneas o el corte se come el texto. */
  avanzar(lineas = 1): this {
    return this.crudo(ESC, 0x64, Math.min(255, Math.max(0, lineas)));
  }

  /**
   * Corta el papel.
   *
   * Usamos `GS V 66 n` (corte con avance) en vez del corte simple porque tiene
   * en cuenta la distancia entre el cabezal y la cuchilla: sin eso, las últimas
   * líneas quedan dentro de la impresora hasta el ticket siguiente.
   *
   * `parcial` deja un puntito de papel sin cortar para que el ticket no caiga
   * al suelo — es lo que hace la mayoría de los comercios.
   */
  cortar(parcial = true): this {
    return this.crudo(GS, 0x56, 0x42, parcial ? 0x01 : 0x00);
  }

  /**
   * Abre el cajón portamonedas.
   *
   * El cajón no tiene cable propio: se enchufa a la impresora con un conector
   * RJ11 y esta le manda un pulso eléctrico. Los tiempos (25 y 250) son los
   * valores estándar; casi ningún cajón necesita otros.
   */
  abrirCajon(pin: 2 | 5 = 2): this {
    return this.crudo(ESC, 0x70, pin === 2 ? 0 : 1, 25, 250);
  }

  /**
   * Imprime una imagen en blanco y negro.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  POR QUÉ ESTO RECIBE BITS Y NO UNA IMAGEN
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Porque convertir un PNG a bits necesita un `<canvas>`, y eso solo existe en
   * el navegador. Este archivo es aritmética pura y se prueba sin navegador: si
   * aquí se cargaran imágenes, las pruebas necesitarían un DOM entero para
   * comprobar una tabla de bytes.
   *
   * La conversión vive en `lib/imagenTicket.ts`. Aquí solo se empaqueta.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  EL FORMATO: `GS v 0`
   * ═══════════════════════════════════════════════════════════════════════════
   *
   *     GS v 0 m xL xH yL yH  d1...dk
   *
   *   · `m = 0` — tamaño normal. Los otros modos duplican la imagen a lo ancho
   *     o a lo alto, y un logo estirado se ve peor que uno pequeño.
   *   · `xL xH` — el ancho **EN BYTES**, no en puntos. Es el error clásico:
   *     mandar 240 en vez de 30 hace que la impresora espere ocho veces más
   *     datos de los que llegan, se quede esperando, y escupa medio metro de
   *     papel en blanco antes de rendirse.
   *   · `yL yH` — el alto en puntos, ese sí.
   *   · Cada byte son ocho puntos horizontales, el bit más alto a la izquierda,
   *     y **1 = negro** (al revés que en una pantalla).
   */
  imagen(anchoEnPuntos: number, alto: number, bits: Uint8Array): this {
    const anchoEnBytes = Math.ceil(anchoEnPuntos / 8);

    // Si los datos no cuadran con el tamaño declarado, la impresora se queda
    // esperando bytes que no van a llegar. Es mejor no imprimir el logo que
    // dejar la caja colgada con el cliente delante.
    if (bits.length !== anchoEnBytes * alto) return this;

    this.crudo(
      GS,
      0x76,
      0x30,
      0x00,
      anchoEnBytes & 0xff,
      (anchoEnBytes >> 8) & 0xff,
      alto & 0xff,
      (alto >> 8) & 0xff,
    );
    this.datos.push(...bits);
    return this;
  }
}

// =============================================================================
//  Utilidades para quien construye tickets
// =============================================================================

/**
 * Recorta un texto y le pone puntos suspensivos si no cabe.
 * Se usa con los nombres de productos largos.
 */
export function recortar(texto: string, largo: number): string {
  if (texto.length <= largo) return texto;
  if (largo <= 1) return texto.slice(0, largo);
  return texto.slice(0, largo - 1) + ".";
}
